import type { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import type { Ranking } from '@upbeat/shared';
import { useRanking } from '../api/hooks';
import { DataTable } from '../components/DataTable';
import { EmptyState, ErrorState } from '../components/EmptyState';
import { Panel } from '../components/Panel';
import { fmt3 } from '../lib/format';
import { isEmptySelection } from '../state/filters';
import { PageShell, usePage } from './PageShell';
import { t } from '../i18n';

type Row = Ranking['rows'][number];

export function Benchmarking() {
  const { state, query } = usePage();
  const empty = state ? isEmptySelection(state) : false;
  const ranking = useRanking(empty ? null : query);
  const d = ranking.data;

  const columns = useMemo<ColumnDef<Row, unknown>[]>(() => [
    { accessorKey: 'rank', header: t.colRank, meta: { num: true, className: 'rank' }, cell: (c) => c.getValue<number>() },
    { accessorKey: 'utility', header: t.colUtility, cell: (c) => <span className="utility">{c.getValue<string>()}</span> },
    { accessorKey: 'country', header: t.colCountry },
    { accessorKey: 'region', header: t.colRegion },
    { accessorKey: 'ownership', header: t.colOwnership, cell: (c) => c.getValue<string | null>() ?? '—' },
    { accessorKey: 'type', header: t.colType, cell: (c) => c.getValue<string | null>() ?? '—' },
    { accessorKey: 'value', header: t.colValue, meta: { num: true }, cell: (c) => fmt3(c.getValue<number>()) },
  ], []);

  const note = d?.ref_year
    ? t.rankedBy(d.indicator, d.ref_year, d.inverted)
    : state?.indicator;

  return (
    <PageShell title={t.benchTitle} note={note}>
      <Panel>
        {ranking.isError ? (
          <ErrorState error={ranking.error} />
        ) : ranking.isPending && !empty ? (
          <div className="skeleton" style={{ height: 480 }} />
        ) : !d?.rows.length || empty ? (
          <EmptyState />
        ) : (
          <DataTable<Row>
            rows={d.rows}
            columns={columns}
            initialSort={[{ id: 'rank', desc: false }]}
            caption={t.rankingCaption(d.rows.length, d.ref_year)}
          />
        )}
      </Panel>
    </PageShell>
  );
}
